// src/components/rewards/GiftsPopup.js
//
// 🎁 GIFTS — every gift box the student has waiting (they were called
// "chests"). Always opens, even with nothing inside, so a child learns how
// gifts (and stickers) are earned instead of pressing a button that does
// nothing.
//
// Props: onClose()

import ModalPortal from "../common/ModalPortal";
import React, { useEffect, useState } from "react";
import ChestArt from "./ChestArt";
import ChestOpener from "./ChestOpener";
import { REWARDS, useChests } from "../../services/rewardsService";
import "./rewards.css";

const FROM = { trail: "Daily trail — day 7", quest: "Treasure Quest", teacher: "From your teacher" };

const GiftsPopup = ({ onClose }) => {
  const { chests, loaded } = useChests();
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !opening && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, opening]);

  return (
    <ModalPortal>
      <div className="sbl-rw-pop" role="dialog" aria-modal="true" aria-label="My gifts" onMouseDown={(e) => e.target === e.currentTarget && !opening && onClose?.()}>
        <div className="sbl-gifts">
          <header className="sbl-rw-head is-gifts">
            <h2>🎁 My Gifts</h2>
            <button type="button" className="sbl-rw-x is-inline" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>

          <div className="sbl-gifts-body">
            {!loaded ? (
              <p className="sbl-gifts-empty">Looking for your gifts…</p>
            ) : chests.length === 0 ? (
              <div className="sbl-gifts-empty">
                <ChestArt tier="wood" className="sbl-gifts-emptyart" />
                <p>
                  <b>No gifts right now.</b> Here's how to get one:
                </p>
              </div>
            ) : (
              <>
                <p className="sbl-gifts-lead">
                  You have <b>{chests.length}</b> gift{chests.length === 1 ? "" : "s"} to open! Tap one.
                </p>
                <div className="sbl-gifts-grid">
                  {chests.map((c) => (
                    <button key={c.id} type="button" className={`sbl-gift is-${c.tier}`} onClick={() => setOpening(c)}>
                      <ChestArt tier={c.tier} />
                      <b>{REWARDS.chests[c.tier]?.name || "Gift"}</b>
                      <small>{c.label || FROM[c.from] || ""}</small>
                      <span className="sbl-gift-open">Open!</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            <div className="sbl-gifts-how">
              <h3>How do I get gifts?</h3>
              <ul>
                <li>
                  <span>🗺️</span> Walk the <b>Daily trail</b> every day you visit — day 7 is a <b>golden gift</b>.
                </li>
                <li>
                  <span>🏴‍☠️</span> Finish a whole <b>Treasure Quest</b> bonus round — you find a <b>wooden gift</b>.
                </li>
                <li>
                  <span>🍎</span> Your <b>teacher</b> can send the class a <b>silver gift</b>.
                </li>
              </ul>
              <h3>What's inside?</h3>
              <p>
                Coins, <b>stickers</b> for your sticker albums 📒, sometimes a power-up ⚡ or something new for your avatar. Stickers <b>only</b> come
                from gifts — fill a whole album to win a special prize!
              </p>
            </div>
          </div>
        </div>
        {opening && <ChestOpener chest={opening} onClose={() => setOpening(null)} />}
      </div>
    </ModalPortal>
  );
};

export default GiftsPopup;
